import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function McpTokensSection({ isPro }) {
  const [tokens, setTokens] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [usageMap, setUsageMap] = useState({}); // token_id -> usage
  const [expandedToken, setExpandedToken] = useState(null);

  // New-token modal
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScopeRead, setNewScopeRead] = useState(true);
  const [newScopeReports, setNewScopeReports] = useState(true);
  const [creating, setCreating] = useState(false);

  // Just-created token raw display
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!isPro) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mcp/tokens`, { withCredentials: true });
      const list = r.data?.tokens || [];
      setTokens(list);
      setLimits(r.data?.rate_limits || null);
      setErr(null);
      // Fetch usage for active tokens in parallel
      const active = list.filter((t) => !t.revoked_at);
      const usageResults = await Promise.allSettled(
        active.map((t) =>
          axios.get(`${API}/mcp/tokens/${t.token_id}/usage`, { withCredentials: true })
            .then((res) => [t.token_id, res.data])
        )
      );
      const map = {};
      for (const u of usageResults) {
        if (u.status === "fulfilled") {
          map[u.value[0]] = u.value[1];
        }
      }
      setUsageMap(map);
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load tokens");
    } finally { setLoading(false); }
  }, [isPro]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setErr("Name is required");
      return;
    }
    const scopes = [];
    if (newScopeRead) scopes.push("read");
    if (newScopeReports) scopes.push("reports");
    setCreating(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/mcp/tokens`, { name: newName.trim(), scopes },
        { withCredentials: true });
      setJustCreated(r.data);
      setShowCreate(false);
      setNewName("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to create token");
    } finally { setCreating(false); }
  };

  const handleRevoke = async (token_id) => {
    if (!window.confirm("Revoke this token? Any client using it will lose access immediately.")) return;
    try {
      await axios.delete(`${API}/mcp/tokens/${token_id}`, { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyRaw = () => {
    if (!justCreated?.raw_token) return;
    navigator.clipboard.writeText(justCreated.raw_token).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!isPro) {
    return (
      <div className="mt-6 border border-zinc-200 rounded-lg p-5 bg-white" data-testid="mcp-tokens-section-free">
        <div className="text-[10px] uppercase font-mono text-zinc-500">API &amp; MCP Access</div>
        <h2 className="mt-1 text-lg font-semibold text-zinc-900">Model Context Protocol</h2>
        <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
          HCRIS+ exposes a Model Context Protocol (MCP) server so LLM clients like Claude Desktop,
          Cursor, and Continue can call our 11 healthcare data tools directly — hospital search,
          benchmarks, OPPS utilization, and full LLM report generation.
        </p>
        <div className="mt-4 text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 inline-block">
          Upgrade to Pro to generate bearer tokens.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 border border-zinc-200 rounded-lg p-5 bg-white" data-testid="mcp-tokens-section-pro">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase font-mono text-zinc-500">API &amp; MCP Access</div>
          <h2 className="mt-1 text-lg font-semibold text-zinc-900">Bearer tokens</h2>
        </div>
        <button onClick={() => { setShowCreate(true); setErr(null); }}
                className="px-3 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-mono uppercase tracking-wider"
                data-testid="mcp-token-generate-btn">
          + Generate token
        </button>
      </div>

      {limits && (
        <div className="mt-3 text-[11px] font-mono text-zinc-500">
          Pro rate limits: <span className="text-zinc-700">{limits.minute}/min</span> ·{" "}
          <span className="text-zinc-700">{limits.hour}/hour</span> ·{" "}
          <span className="text-zinc-700">{limits.day}/day</span> · report_generate counts 5×.
        </div>
      )}

      {err && <div className="mt-3 text-xs text-red-700 font-mono" data-testid="mcp-token-error">{err}</div>}

      {/* Just-created raw token banner */}
      {justCreated && (
        <div className="mt-4 border border-amber-300 bg-amber-50 rounded p-4" data-testid="mcp-token-raw-banner">
          <div className="text-[10px] uppercase font-mono text-amber-800 font-semibold">
            Save this token now — you won&apos;t see it again
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 text-xs font-mono break-all bg-white border border-amber-200 rounded px-2 py-1.5 text-zinc-900"
                  data-testid="mcp-token-raw-value">
              {justCreated.raw_token}
            </code>
            <button onClick={copyRaw}
                    className="px-3 py-1.5 rounded bg-amber-700 hover:bg-amber-800 text-white text-[10px] font-mono uppercase tracking-wider"
                    data-testid="mcp-token-copy-btn">
              {copied ? "Copied" : "Copy"}
            </button>
            <button onClick={() => setJustCreated(null)}
                    className="px-2 py-1.5 rounded bg-zinc-200 hover:bg-zinc-300 text-zinc-700 text-[10px] font-mono uppercase"
                    data-testid="mcp-token-dismiss-btn">
              Dismiss
            </button>
          </div>
          <div className="mt-2 text-[11px] font-mono text-amber-800">
            Use as <code className="text-zinc-900">Authorization: Bearer {justCreated.raw_token.slice(0, 18)}…</code>
          </div>
        </div>
      )}

      {/* Tokens table */}
      <div className="mt-4">
        {loading ? (
          <div className="text-xs font-mono text-zinc-500">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="text-xs font-mono text-zinc-500" data-testid="mcp-token-empty">
            No tokens yet. Generate one to connect Claude Desktop, Cursor, or your own MCP client.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="mcp-token-table">
            <thead>
              <tr className="text-left text-[10px] uppercase font-mono text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-2">Name</th>
                <th className="py-2 pr-2">Prefix</th>
                <th className="py-2 pr-2">Scopes</th>
                <th className="py-2 pr-2">Created</th>
                <th className="py-2 pr-2">Last used</th>
                <th className="py-2 pr-2">Last 24h</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const u = usageMap[t.token_id];
                const isExpanded = expandedToken === t.token_id;
                return (
                <React.Fragment key={t.token_id}>
                <tr className="border-b border-zinc-100 font-mono"
                    data-testid={`mcp-token-row-${t.token_id}`}>
                  <td className="py-2 pr-2 text-zinc-900">{t.name}</td>
                  <td className="py-2 pr-2 text-zinc-500">{t.prefix}…</td>
                  <td className="py-2 pr-2 text-zinc-600">{(t.scopes || []).join(", ")}</td>
                  <td className="py-2 pr-2 text-zinc-500">{fmtRel(t.created_at)}</td>
                  <td className="py-2 pr-2 text-zinc-500">{fmtRel(t.last_used_at)}</td>
                  <td className="py-2 pr-2">
                    {u ? (
                      <button
                        onClick={() => setExpandedToken(isExpanded ? null : t.token_id)}
                        className="text-zinc-700 hover:text-zinc-900 underline decoration-dotted"
                        data-testid={`mcp-token-usage-${t.token_id}`}>
                        {u.calls_last_24h} {isExpanded ? "▾" : "▸"}
                      </button>
                    ) : t.revoked_at ? <span className="text-zinc-400">—</span> : <span className="text-zinc-400">…</span>}
                  </td>
                  <td className="py-2 pr-2">
                    {t.revoked_at ? (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">Revoked</span>
                    ) : (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Active</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {!t.revoked_at && (
                      <button onClick={() => handleRevoke(t.token_id)}
                              className="text-[10px] uppercase text-red-700 hover:text-red-900 font-mono"
                              data-testid={`mcp-token-revoke-${t.token_id}`}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
                {isExpanded && u && (
                  <tr className="bg-zinc-50 border-b border-zinc-200" data-testid={`mcp-token-usage-detail-${t.token_id}`}>
                    <td colSpan={8} className="py-3 px-4 font-mono text-[11px] text-zinc-700">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        <div>
                          <div className="text-[10px] uppercase text-zinc-500 mb-1">Activity</div>
                          <div>Last 24h: <span className="text-zinc-900">{u.calls_last_24h}</span> calls
                               {u.errors_last_24h > 0 && <span className="text-red-700"> ({u.errors_last_24h} errors)</span>}</div>
                          <div>Last 7d:  <span className="text-zinc-900">{u.calls_last_7d}</span> calls</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-zinc-500 mb-1">Rate-limit remaining</div>
                          <div>Minute: <span className="text-zinc-900">{u.rate_limit_remaining?.minute}</span> / {u.limits?.minute}</div>
                          <div>Hour:   <span className="text-zinc-900">{u.rate_limit_remaining?.hour}</span> / {u.limits?.hour}</div>
                          <div>Day:    <span className="text-zinc-900">{u.rate_limit_remaining?.day}</span> / {u.limits?.day}</div>
                        </div>
                        <div className="col-span-2 mt-2">
                          <div className="text-[10px] uppercase text-zinc-500 mb-1">By tool (last 24h)</div>
                          {Object.keys(u.by_tool || {}).length === 0 ? (
                            <div className="text-zinc-400">No calls yet</div>
                          ) : (
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {Object.entries(u.by_tool).map(([tool, n]) => (
                                <span key={tool}><span className="text-zinc-500">{tool}</span> <span className="text-zinc-900">{n}</span></span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );})}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 text-[11px] font-mono text-zinc-500">
        Setup guide:{" "}
        <a href="/docs/MCP_CLAUDE_DESKTOP.md" target="_blank" rel="noreferrer"
           className="underline hover:text-zinc-700">
          Claude Desktop, Cursor, Continue
        </a>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
             data-testid="mcp-token-create-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-zinc-900">Generate MCP bearer token</h3>
            <p className="mt-1 text-xs text-zinc-500 font-mono">
              The full token will be shown ONCE on the next screen. Save it then.
            </p>
            <div className="mt-4">
              <label className="text-[10px] uppercase font-mono text-zinc-500">Name</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                     placeholder="Claude Desktop laptop"
                     className="mt-1 w-full border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                     data-testid="mcp-token-name-input" />
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase font-mono text-zinc-500">Scopes</div>
              <label className="flex items-center gap-2 mt-2 text-sm font-mono text-zinc-700">
                <input type="checkbox" checked={newScopeRead} onChange={(e) => setNewScopeRead(e.target.checked)}
                       data-testid="mcp-token-scope-read" />
                <span><strong>read</strong> · all 10 read tools (search, indicators, benchmarks, OPPS, quality)</span>
              </label>
              <label className="flex items-center gap-2 mt-1 text-sm font-mono text-zinc-700">
                <input type="checkbox" checked={newScopeReports} onChange={(e) => setNewScopeReports(e.target.checked)}
                       data-testid="mcp-token-scope-reports" />
                <span><strong>reports</strong> · report_generate (5× rate-limit weight)</span>
              </label>
            </div>
            {err && <div className="mt-3 text-xs text-red-700 font-mono">{err}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setShowCreate(false); setErr(null); }}
                      className="px-4 py-2 rounded bg-zinc-200 hover:bg-zinc-300 text-zinc-700 text-xs font-mono uppercase"
                      data-testid="mcp-token-cancel-btn">
                Cancel
              </button>
              <button onClick={handleCreate} disabled={creating}
                      className="px-4 py-2 rounded bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white text-xs font-mono uppercase tracking-wider"
                      data-testid="mcp-token-create-confirm-btn">
                {creating ? "Creating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
